import * as THREE from 'three';
import { multiplicadorDe, bonoDe } from '../items/Rareza.js';

// ============================================================
// Combat.js - Player melee combat system
// Left-click attack, damage calculation, knockback, critical hits
// ============================================================

// Tool/weapon damage values
const WEAPON_DAMAGE = {
  // Fist / no tool
  FIST: 1,

  // Swords (primary weapons)
  WOOD_SWORD: 5,
  STONE_SWORD: 6,
  IRON_SWORD: 7,
  DIAMOND_SWORD: 8,

  // Axes (secondary melee)
  WOOD_AXE: 4,
  STONE_AXE: 5,
  IRON_AXE: 6,
  DIAMOND_AXE: 7,

  // Pickaxes (weak melee)
  WOOD_PICKAXE: 2,
  STONE_PICKAXE: 3,
  IRON_PICKAXE: 4,
  DIAMOND_PICKAXE: 5,

  // Shovels (very weak melee)
  WOOD_SHOVEL: 1,
  STONE_SHOVEL: 2,
  IRON_SHOVEL: 3,
  DIAMOND_SHOVEL: 4,
};

// Attack reach in blocks
const ATTACK_REACH = 3.0;

// Attack cooldown in seconds
const ATTACK_COOLDOWN = 0.5;

// Critical hit multiplier (when falling)
const CRITICAL_MULTIPLIER = 1.5;

// Knockback force
const KNOCKBACK_HORIZONTAL = 6.0;
const KNOCKBACK_VERTICAL = 5.5;

// Hitstop: brief pause on impact for weight/impact feel
const HITSTOP_DURATION = 0.05; // 3 frames at 60fps

// Camera punch effect parameters
const PUNCH_ZOOM_AMOUNT = 3;       // FOV decrease in degrees
const PUNCH_DURATION = 0.15;       // seconds

// Temp vectors to avoid allocations
const _rayOrigin = new THREE.Vector3();
const _rayDir = new THREE.Vector3();
const _knockDir = new THREE.Vector3();
const _entityCenter = new THREE.Vector3();

export class Combat {
  constructor() {
    // Attack cooldown timer
    this.cooldownTimer = 0;

    // Camera punch animation state
    this.punchTimer = 0;
    this.isPunching = false;

    // Hitstop state
    this.hitstopTimer = 0;
    this.isHitstop = false;

    // Stats tracking
    this.totalDamageDealt = 0;
    this.totalKills = 0;
    this.lastHitEntity = null;
  }

  /**
   * Update the combat system each frame.
   * Call this every frame to tick cooldowns and punch animation.
   * @param {number} dt - Delta time in seconds
   * @returns {{ fovOffset: number }} Camera effects to apply
   */
  update(dt) {
    // Hitstop: freeze game time briefly on impact
    if (this.isHitstop) {
      this.hitstopTimer -= dt;
      if (this.hitstopTimer <= 0) {
        this.isHitstop = false;
        this.hitstopTimer = 0;
      }
      // During hitstop, return freeze flag so game loop can skip physics
      return { fovOffset: 0, hitstop: true };
    }

    // Tick attack cooldown
    if (this.cooldownTimer > 0) {
      this.cooldownTimer -= dt;
      if (this.cooldownTimer < 0) this.cooldownTimer = 0;
    }

    // Camera punch animation
    let fovOffset = 0;
    if (this.isPunching) {
      this.punchTimer -= dt;
      if (this.punchTimer <= 0) {
        this.isPunching = false;
        this.punchTimer = 0;
      } else {
        // Smooth punch: zoom in then return
        const t = 1 - (this.punchTimer / PUNCH_DURATION);
        // Quick in, slow out
        const punch = t < 0.3
          ? (t / 0.3)
          : 1 - ((t - 0.3) / 0.7);
        fovOffset = -PUNCH_ZOOM_AMOUNT * punch;
      }
    }

    return { fovOffset, hitstop: false };
  }

  /**
   * Check if the player can attack right now.
   * @returns {boolean}
   */
  canAttack() {
    return this.cooldownTimer <= 0;
  }

  /**
   * Get the cooldown progress (0 = ready, 1 = just attacked).
   * Useful for UI indicators.
   * @returns {number} 0 to 1
   */
  getCooldownProgress() {
    if (this.cooldownTimer <= 0) return 0;
    return this.cooldownTimer / ATTACK_COOLDOWN;
  }

  /**
   * Attempt a melee attack.
   *
   * @param {THREE.Vector3} playerPos - Player eye position
   * @param {THREE.Vector3} lookDir - Normalized look direction (camera forward)
   * @param {Array} entities - Array of entity objects (each must have .position, .health, .type, .velocity, .mesh)
   * @param {string|null} equippedItem - Name of equipped item/tool, or null for fist
   * @param {boolean} isFalling - True if player is falling (for critical hits)
   * @returns {{ hit: boolean, entity: object|null, damage: number, critical: boolean, killed: boolean }}
   */
  tryAttack(playerPos, lookDir, entities, equippedItem, isFalling = false) {
    const result = {
      hit: false,
      entity: null,
      damage: 0,
      critical: false,
      killed: false,
    };

    // Check cooldown
    if (!this.canAttack()) {
      return result;
    }

    // Start cooldown and punch animation regardless of hit
    this.cooldownTimer = ATTACK_COOLDOWN;
    this._startPunch();

    // Determine base damage from equipped item
    let baseDamage = WEAPON_DAMAGE.FIST;
    if (equippedItem && typeof equippedItem === 'string') {
      const key = equippedItem.toUpperCase().replace(/\s+/g, '_');
      if (WEAPON_DAMAGE[key] !== undefined) {
        baseDamage = WEAPON_DAMAGE[key];
      }
    } else if (equippedItem && typeof equippedItem === 'number') {
      // If equipped item is passed as a damage number directly
      baseDamage = equippedItem;
    }

    /* La rareza y el bono de «Daño» del arma equipada.

       Esto es lo que hace que el botín no sea una etiqueta de colores: una
       espada de hierro legendaria con +27% de daño pega de verdad más que una
       espada de hierro común. Sin esto, D1 sería decoración. */
    if (equippedItem && typeof equippedItem === 'object') {
      baseDamage = baseDamage * multiplicadorDe(equippedItem)
                             * (1 + bonoDe(equippedItem, 'dano') / 100);
    }

    // Critical hit check: player must be falling (velocity.y < 0 and not on ground)
    let damage = baseDamage;
    let isCritical = false;
    if (isFalling) {
      damage = Math.ceil(baseDamage * CRITICAL_MULTIPLIER);
      isCritical = true;
    }

    // Set up ray from player eye position along look direction
    _rayOrigin.copy(playerPos);
    _rayDir.copy(lookDir).normalize();

    // Find the closest entity in the attack cone
    let closestEntity = null;
    let closestDist = ATTACK_REACH + 1;

    for (const entity of entities) {
      if (!entity.position || entity.health === undefined) continue;
      if (entity.health <= 0) continue; // already dead

      // Calculate entity center (position is at feet, so offset up by ~1 block)
      _entityCenter.copy(entity.position);
      _entityCenter.y += 0.9; // approximate center of mass

      // Vector from player to entity center
      const toEntity = _entityCenter.clone().sub(_rayOrigin);
      const distAlongRay = toEntity.dot(_rayDir);

      // Must be in front of player and within reach
      if (distAlongRay < 0 || distAlongRay > ATTACK_REACH) continue;

      // Calculate perpendicular distance from ray to entity center
      const closestPoint = _rayOrigin.clone().add(_rayDir.clone().multiplyScalar(distAlongRay));
      const perpDist = closestPoint.distanceTo(_entityCenter);

      // Hit box size - roughly 0.8 block radius for humanoids, adjustable
      const hitRadius = _getHitRadius(entity.type);
      if (perpDist > hitRadius) continue;

      // Prefer the closest entity
      if (distAlongRay < closestDist) {
        closestDist = distAlongRay;
        closestEntity = entity;
      }
    }

    if (!closestEntity) {
      return result;
    }

    // Apply damage
    closestEntity.health -= damage;
    result.hit = true;
    result.entity = closestEntity;
    result.damage = damage;
    result.critical = isCritical;
    this.totalDamageDealt += damage;
    this.lastHitEntity = closestEntity;

    // Knockback
    _knockDir.subVectors(closestEntity.position, playerPos);
    _knockDir.y = 0;
    _knockDir.normalize();

    if (closestEntity.velocity) {
      closestEntity.velocity.x += _knockDir.x * KNOCKBACK_HORIZONTAL;
      closestEntity.velocity.y += KNOCKBACK_VERTICAL;
      closestEntity.velocity.z += _knockDir.z * KNOCKBACK_HORIZONTAL;
    }

    // Hitstop: brief freeze for impact feel
    this.isHitstop = true;
    this.hitstopTimer = HITSTOP_DURATION;

    // Hurt flash: briefly tint the entity red
    if (closestEntity.mesh) {
      _hurtFlash(closestEntity.mesh);
    }

    // Check if killed
    if (closestEntity.health <= 0) {
      result.killed = true;
      this.totalKills++;
    }

    return result;
  }

  /**
   * Start the camera punch animation.
   * @private
   */
  _startPunch() {
    this.isPunching = true;
    this.punchTimer = PUNCH_DURATION;
  }

  /**
   * Reset combat state (e.g., on respawn).
   */
  reset() {
    this.cooldownTimer = 0;
    this.punchTimer = 0;
    this.isPunching = false;
  }

  /**
   * Get combat stats for display.
   * @returns {{ damageDealt: number, kills: number }}
   */
  getStats() {
    return {
      damageDealt: this.totalDamageDealt,
      kills: this.totalKills,
    };
  }
}

// ==================== INTERNAL HELPERS ====================

/**
 * Get the hit radius for a given entity type.
 * Larger mobs have bigger hitboxes.
 * @param {string} type - Entity type name
 * @returns {number} Hit radius in blocks
 */
function _getHitRadius(type) {
  switch (type) {
    case 'SPIDER':
      return 1.0;  // wider body
    case 'ZOMBIE':
    case 'SKELETON':
    case 'VILLAGER':
    case 'STEVE':
    case 'ALEX':
    case 'HEROBRINE':
      return 0.8;  // humanoid
    case 'CREEPER':
      return 0.7;  // narrower
    case 'COW':
    case 'SHEEP':
      return 0.9;  // large animals
    case 'PIG':
      return 0.7;
    case 'CHICKEN':
      return 0.5;  // small
    default:
      return 0.8;
  }
}

/**
 * Apply a brief red flash to an entity mesh to indicate damage.
 * @param {THREE.Object3D} mesh - The entity's root mesh group
 */
function _hurtFlash(mesh) {
  const origColors = [];

  mesh.traverse(child => {
    if (child.isMesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of mats) {
        if (mat.color) {
          origColors.push({ mat, hex: mat.color.getHex() });
          mat.color.setHex(0xFF3333);
        }
      }
    }
  });

  // Restore after 150ms
  setTimeout(() => {
    for (const { mat, hex } of origColors) {
      if (mat.color) {
        mat.color.setHex(hex);
      }
    }
  }, 150);
}

// Export weapon damage table for UI / other systems
export { WEAPON_DAMAGE, ATTACK_REACH, ATTACK_COOLDOWN, CRITICAL_MULTIPLIER, KNOCKBACK_VERTICAL, HITSTOP_DURATION };
